"use client";

import React, { Fragment, useEffect, useRef, useState } from "react";

import Kpi50 from "@/components/Editorial2Col/Kpi50";
import Image from "@/components/ui/Image/Image";
import useMediaQuery from "@/hooks/useMediaQuery";

import { EditorialKpi, EditorialModuleImage as EditorialModuleImageType } from "./type";

export interface EditorialModuleImageProps {
  entry: EditorialModuleImageType;
  kpi?: EditorialKpi[];
  slideIndex?: number;
  isMapLocation?: boolean;
  isMapOnly?: boolean;
}

type LocationPoint = {
  id: string;
  locationXAxis: number;
  locationYAxis: number;
  title: string;
};

export default function EditorialModuleImage({ entry, kpi, isMapOnly }: EditorialModuleImageProps) {
  const isMobile = useMediaQuery("(max-width: 600px)");
  const isDesktop = useMediaQuery("(min-width: 1200px)");
  const [hoveredLocation, setHoveredLocation] = useState<LocationPoint | null>(null);
  const [clickedLocation, setClickedLocation] = useState<LocationPoint | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const activeLocation = isMobile ? clickedLocation : clickedLocation || hoveredLocation;

  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (svgRef.current && !svgRef.current.contains(event.target as Node)) {
        setClickedLocation(null);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (scrollContainerRef.current && isMobile) {
      const container = scrollContainerRef.current;
      container.scrollLeft = (container.scrollWidth - container.clientWidth) / 2;
    }
  }, [isMobile]);

  if (entry?.isMapLocation && isDesktop) {
    return (
      <div
        ref={scrollContainerRef}
        style={{
          overflowX: isMobile ? "scroll" : "visible",
          width: isMobile ? "100%" : isMapOnly ? "1000" : "800",
        }}
      >
        <svg
          ref={svgRef}
          id="svgMap"
          width={`${isMobile ? "1200" : isMapOnly ? "1000" : "800"}`}
          height={`${isMobile ? "500" : 500}`}
          viewBox={`0 0 ${isMapOnly ? "1000" : "800"} 500`}
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <filter id="circleShadow" x="-50%" y="-50%" width="200%" height="200%">
              <feDropShadow dx="0" dy="0" stdDeviation="4" floodColor="#DDD0B0" floodOpacity="1" />
            </filter>
          </defs>
          <image href={entry.image.url} width={isMapOnly ? "1000" : "800"} height={500} />
          <rect width="100%" height="100%" fill="transparent" />

          {entry?.locationPoint?.map((location: any) => (
            <circle
              key={location.id}
              className="blinking-dot"
              cx={location.locationXAxis}
              cy={location.locationYAxis}
              r="6"
              fill="#B29C69"
              filter="url(#circleShadow)"
              onClick={() => {
                if (clickedLocation?.id === location.id) {
                  setClickedLocation(null);
                } else {
                  setClickedLocation(location);
                }
              }}
              onMouseEnter={() => setHoveredLocation(location)}
              onMouseLeave={() => setHoveredLocation(null)}
            />
          ))}

          {activeLocation && (
            <g style={{ pointerEvents: "none" }}>
              <rect
                x={activeLocation.locationXAxis + 8}
                y={activeLocation.locationYAxis - (isMobile ? 28 : 36)}
                width={activeLocation.title.length * (isMobile ? 8 : 10) + 20}
                height={isMobile ? 24 : 32}
                fill="white"
                stroke="gray"
                rx={4}
                ry={4}
              />
              <text
                x={activeLocation.locationXAxis + 16}
                y={activeLocation.locationYAxis - (isMobile ? 12 : 14)}
                fontSize={isMobile ? 12 : 18}
                // fontWeight="bold"
                fill="#000"
                style={{ pointerEvents: "none" }}
              >
                {activeLocation.title}
              </text>
            </g>
          )}
        </svg>
      </div>
    );
  }
  return (
    <div className="relative h-full">
      {/* <Image
        className="absolute right-0 mt-m mr-m md:w-[110px] md:mt-xl md:mr-[40px] z-10"
        src={"/assets/damac-d.svg"}
        width={76}
        height={46}
        alt="logo damac"
      /> */}
      {entry?.mobileImage?.url.includes("mp4") ? (
        <video
          className="aspect-9-16 w-full  rounded-lg shadow-elevation object-cover"
          poster={entry.image.url}
          muted
          autoPlay
          loop
          playsInline
          // {...inspectorMode.getProps("video")}
        >
          {entry?.mobileImage?.url && <source src={entry.mobileImage.url} type="video/mp4" />}
        </video>
      ) : (
        <div className="relative">
          <Image
            className="aspect-1-1 md:aspect-auto object-contian w-full h-full md:block"
            src={entry.image.url}
            width={375}
            height={375}
            alt={entry.image.title}
          />
          {/* img-overlay-2 removed from below div */}
          <div className="absolute w-full h-full top-0 left-0 inset-0 md:hidden"></div>
        </div>
      )}
      {kpi && <Kpi50 kpi={kpi} />}
    </div>
  );
}
